import * as Blockly from 'blockly/core';
import { useBlockModalLedBarStore } from '@/stores/useBlockModalLedBarStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { darken } from '@/(routers)/boards/editor/block/blockTheme';

export interface LedBarStepData {
    leds: boolean[]; // index 0〜9 = LED 1〜10
    duration: number;
}

const FIELD_HEIGHT = 42;
const FIELD_MIN_WIDTH = 90;
const SWATCH_SIZE = 24;
const SWATCH_RADIUS = 4;
const SWATCH_GAP = 6;
const PADDING = 8;
const SWATCH_FONT_SIZE = 11;

// 点灯数(0〜10)に応じた赤: 0=黒、1=やや暗赤、10=明赤（明度で表現）
function getLedCountColor(count: number): string {
    if (count === 0) return 'rgb(20,20,20)';
    const t = (count - 1) / 9;
    const r = Math.round(120 + (229 - 120) * t);
    const g = Math.round(20 + (57 - 20) * t);
    const b = Math.round(20 + (53 - 20) * t);
    return `rgb(${r},${g},${b})`;
}

export class FieldLedBar extends Blockly.Field<string> {
    private steps_: LedBarStepData[];
    private labelEl_: SVGTextElement | null = null;
    private swatchEls_: SVGElement[] = [];

    static override fromJson(options: Blockly.FieldConfig & { steps?: LedBarStepData[] }): FieldLedBar {
        return new FieldLedBar(options.steps ?? []);
    }

    constructor(steps: LedBarStepData[]) {
        super(JSON.stringify(steps));
        this.steps_ = steps;
        this.SERIALIZABLE = true;
    }

    override initView(): void {
        const width = this.calcWidth_();

        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width, height: FIELD_HEIGHT },
            this.fieldGroup_,
        );

        this.labelEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.TEXT,
            {
                x: PADDING,
                y: FIELD_HEIGHT / 2 + 6,
                'font-size': '17px',
                'font-weight': '500',
                'font-family': 'Corporate Logo, sans-serif',
                style: 'fill: white !important;',
            },
            this.fieldGroup_,
        ) as SVGTextElement;

        this.updateDisplay_();

        requestAnimationFrame(() => {
            this.applyStyle_();
            this.updateDisplay_();
            (this.getSourceBlock() as Blockly.BlockSvg)?.render();
            this.fieldGroup_?.addEventListener('mouseenter', () => this.applyStyle_(true));
            this.fieldGroup_?.addEventListener('mouseleave', () => this.applyStyle_(false));
        });
    }

    private applyStyle_(hover = false): void {
        if (!this.borderRect_) return;
        const colour = this.getSourceBlock()?.getColour() ?? '#e65100';
        const fillColour = this.steps_.length === 0 ? darken(colour) : colour;
        this.borderRect_.setAttribute(
            'style',
            `fill: ${fillColour} !important; stroke: ${
                hover ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.3)'
            }; stroke-width: ${hover ? 2 : 1};`,
        );
    }

    private calcWidth_(): number {
        if (this.steps_.length === 0) return FIELD_MIN_WIDTH;
        return PADDING + this.steps_.length * (SWATCH_SIZE + SWATCH_GAP) - SWATCH_GAP + PADDING;
    }

    private updateDisplay_(): void {
        if (!this.fieldGroup_ || !this.labelEl_) return;

        this.swatchEls_.forEach((el) => el.parentNode?.removeChild(el));
        this.swatchEls_ = [];

        if (this.steps_.length === 0) {
            this.labelEl_.setAttribute('visibility', 'visible');
            this.labelEl_.textContent = '設定した光';
        } else {
            this.labelEl_.setAttribute('visibility', 'hidden');

            const swatchY = (FIELD_HEIGHT - SWATCH_SIZE) / 2;
            this.steps_.forEach((step, i) => {
                const x = PADDING + i * (SWATCH_SIZE + SWATCH_GAP);
                const count = step.leds.filter(Boolean).length;
                const isOff = count === 0;
                const color = getLedCountColor(count);

                const rect = Blockly.utils.dom.createSvgElement(
                    Blockly.utils.Svg.RECT,
                    {
                        x,
                        y: swatchY,
                        width: SWATCH_SIZE,
                        height: SWATCH_SIZE,
                        rx: SWATCH_RADIUS,
                        ry: SWATCH_RADIUS,
                        style: `fill: ${isOff ? 'rgba(0,0,0,0.3)' : color} !important; stroke: ${
                            isOff ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.3)'
                        }; stroke-width: 1;`,
                    },
                    this.fieldGroup_,
                );

                if (!isOff) {
                    this.swatchEls_.push(rect);
                } else {
                    const text = Blockly.utils.dom.createSvgElement(
                        Blockly.utils.Svg.TEXT,
                        {
                            x: x + SWATCH_SIZE / 2,
                            y: swatchY + SWATCH_SIZE / 2 + SWATCH_FONT_SIZE / 2 - 1,
                            'font-size': `${SWATCH_FONT_SIZE}px`,
                            'font-weight': '600',
                            'text-anchor': 'middle',
                            style: 'fill: rgba(255,255,255,0.4) !important;',
                        },
                        this.fieldGroup_,
                    ) as SVGTextElement;
                    text.textContent = '消';
                    this.swatchEls_.push(rect, text);
                }
            });
        }

        const width = this.calcWidth_();
        this.borderRect_?.setAttribute('width', String(width));
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        this.applyStyle_();
        (this.getSourceBlock() as Blockly.BlockSvg)?.render();
    }

    public setSteps(steps: LedBarStepData[]): void {
        const oldJson = JSON.stringify(this.steps_);
        this.steps_ = steps;
        const json = JSON.stringify(steps);
        this.setValue(json);
        const block = this.getSourceBlock();
        if (block) {
            Blockly.Events.fire(new Blockly.Events.BlockChange(block, 'field', this.name ?? '', oldJson, json));
        }
    }

    public getSteps(): LedBarStepData[] {
        return this.steps_;
    }

    override doValueUpdate_(newValue: string): void {
        try {
            const parsed = JSON.parse(newValue);
            if (
                Array.isArray(parsed) &&
                parsed.every((s) => Array.isArray(s?.leds) && typeof s?.duration === 'number')
            ) {
                this.steps_ = parsed as LedBarStepData[];
            } else {
                this.steps_ = [];
            }
        } catch {
            this.steps_ = [];
        }
        if (this.fieldGroup_) {
            this.updateDisplay_();
        }
    }

    override getValue(): string {
        return JSON.stringify(this.steps_);
    }

    override getSize(): Blockly.utils.Size {
        const width = this.calcWidth_();
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        return this.size_;
    }

    override showEditor_(): void {
        const { setFieldRef } = useBlockModalLedBarStore.getState();
        const { openModal } = useBlockModalStore.getState();
        setFieldRef({ setSteps: (steps) => this.setSteps(steps), getSteps: () => this.getSteps() });
        openModal('led_bar');
    }
}
